import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useLanguage } from '../contexts/LanguageContext';
import onboardingImage from '../components/onboarding_image.png';
import welcomeImage from '../components/welcome.png';
import '../styles/OnboardingPage.css';

function OnboardingPage() {
  const { language, toggleLanguage } = useLanguage();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordReqs, setShowPasswordReqs] = useState(false);
  const { selectedWorkspaceId, authUserId } = useWorkspace();
  const navigate = useNavigate();
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companyName, setCompanyName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');

  useEffect(() => {
    const fetchClientData = async () => {
      if (selectedWorkspaceId) {
        try {
          const { data, error } = await supabase
            .from('clients')
            .select('id, name, company_onboarding_completed, website_url, company_address')
            .eq('id', selectedWorkspaceId)
            .single();

          if (error) {
            console.error('Error fetching client data:', error);
          } else {
            setClientData(data);
            if (data) {
              setCompanyName(data.name || '');
              setWebsiteUrl(data.website_url || '');
              setCompanyAddress(data.company_address || '');
            }
          }
        } catch (error) {
          console.error('Error in client data fetch:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchClientData();
  }, [selectedWorkspaceId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!firstName || !lastName) {
      setError(language === 'en' ? 'Please fill in all fields' : 'Bitte füllen Sie alle Felder aus');
      return;
    }

    try {
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({
          firstname: firstName,
          lastname: lastName,
          fullname: `${firstName} ${lastName}`,
          onboarding_completed: new Date().toISOString()
        })
        .eq('id', authUserId);

      if (updateError) throw updateError;

      // If password is set, update it
      if (password) {
        const { error: passwordError } = await supabase.auth.updateUser({
          password: password
        });

        if (passwordError) throw passwordError;
      }

      navigate('/dashboard');
    } catch (error) {
      setError(error.message);
    }
  };

  const handleContinue = () => {
    setCurrentStep(currentStep + 1);
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const getTotalSteps = () => {
    if (clientData && clientData.company_onboarding_completed !== null) {
      return 2;
    }
    return 4;
  };

  const handleCompanyDetailsSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!companyName) {
      setError(language === 'en' ? 'Please enter a company name' : 'Bitte geben Sie einen Firmennamen ein');
      return;
    }

    try {
      const { error: updateError } = await supabase
        .from('clients')
        .update({
          name: companyName,
          website_url: websiteUrl,
          company_address: companyAddress
        })
        .eq('id', selectedWorkspaceId);

      if (updateError) throw updateError;

      handleContinue();
    } catch (error) {
      setError(error.message);
    }
  };

  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      // Update user profile
      const { error: userProfileError } = await supabase
        .from('user_profiles')
        .update({
          firstname: firstName,
          lastname: lastName,
          fullname: `${firstName} ${lastName}`,
          onboarding_completed: new Date().toISOString()
        })
        .eq('id', authUserId);

      if (userProfileError) throw userProfileError;

      // Update client
      const { error: clientError } = await supabase
        .from('clients')
        .update({
          name: companyName,
          website_url: websiteUrl,
          company_address: companyAddress,
          company_onboarding_completed: new Date().toISOString()
        })
        .eq('id', selectedWorkspaceId);

      if (clientError) throw clientError;

      // If password is set, update it
      if (password) {
        const { error: passwordError } = await supabase.auth.updateUser({
          password: password
        });

        if (passwordError) throw passwordError;
      }

      navigate('/dashboard');
    } catch (error) {
      setError(error.message);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <div className="profile-setup-content">
              <div className="language-toggle-container">
                <button 
                  className="language-toggle"
                  onClick={toggleLanguage}
                >
                  {language === 'en' ? '🇬🇧' : '🇩🇪'}
                </button>
              </div>
              
              {loading ? (
                <h1>{language === 'en' ? 'Welcome to Morrow Collective 👋' : 'Willkommen bei Morrow Collective 👋'}</h1>
              ) : (
                clientData && clientData.company_onboarding_completed !== null ? (
                  <h1>
                    {language === 'en' 
                      ? `Morrow Collective 🤝 ${clientData.name}` 
                      : `Morrow Collective 🤝 ${clientData.name}`}
                  </h1>
                ) : (
                  <h1>{language === 'en' ? 'Welcome to Morrow Collective 👋' : 'Willkommen bei Morrow Collective 👋'}</h1>
                )
              )}
              <h2>{language === 'en' ? 'Where non-dilutive funding meets simplicity' : 'Wo nicht-dilutive Finanzierung auf Einfachheit trifft'}</h2>
              
              <div className="welcome-content-wrapper">
                <div className="welcome-illustration">
                  <img 
                    src={onboardingImage} 
                    alt="Collaboration illustration showing people working on a shared digital platform" 
                    className="onboarding-image"
                  />
                </div>
                
                <div className="welcome-text">
                  <p>{language === 'en' 
                    ? 'Welcome to the all-in-one space for our collaboration on public funding. Share documents, provide feedback, or create reports with just a few clicks.'
                    : 'Willkommen im All-in-One-Bereich für unsere Zusammenarbeit bei der öffentlichen Finanzierung. Teilen Sie Dokumente, geben Sie Feedback oder erstellen Sie Berichte mit nur wenigen Klicks.'}</p>
                  <p>{language === 'en' 
                    ? 'We are doing our very best to make every step super simple, so you can focus on what matters most... 🚀'
                    : 'Wir geben unser Bestes, um jeden Schritt super einfach zu gestalten, damit Sie sich auf das Wesentliche konzentrieren können... 🚀'}</p>
                </div>
              </div>
              
              <div className="navigation-buttons">
                <div className="step-indicator">
                  <p>
                    {language === 'en' 
                      ? `Step 1 of ${getTotalSteps()}` 
                      : `Schritt 1 von ${getTotalSteps()}`}
                  </p>
                </div>
                <div className="buttons">
                  <button className="continue-button" onClick={handleContinue}>
                    {language === 'en' ? 'Continue' : 'Weiter'} <span>&gt;</span>
                  </button>
                </div>
              </div>
            </div>
          </>
        );
      case 2:
        return (
          <>
            <div className="profile-setup-content">
              <div className="language-toggle-container">
                <button 
                  className="language-toggle"
                  onClick={toggleLanguage}
                >
                  {language === 'en' ? '🇬🇧' : '🇩🇪'}
                </button>
              </div>
              <h1>{language === 'en' ? 'Let\'s make it your account' : 'Machen wir es zu Ihrem Konto'}</h1>
              <h2>{language === 'en' ? 'Complete your profile' : 'Vervollständigen Sie Ihr Profil'}</h2>

              <div className="profile-form-exact">
                {error && <p className="error-message">{error}</p>}

                <div className="form-row">
                  <div className="form-field">
                    <label htmlFor="firstname">{language === 'en' ? 'Firstname' : 'Vorname'}</label>
                    <input
                      id="firstname"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-field">
                    <label htmlFor="lastname">{language === 'en' ? 'Lastname' : 'Nachname'}</label>
                    <input
                      id="lastname"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label htmlFor="password">{language === 'en' ? 'Password' : 'Passwort'}</label>
                  <div className="password-input-container">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onFocus={() => setShowPasswordReqs(true)}
                      onBlur={() => setShowPasswordReqs(false)}
                    />
                    <button 
                      type="button" 
                      className="toggle-password" 
                      onClick={togglePasswordVisibility}
                    >
                      {showPassword ? "👁️" : "👁️‍🗨️"}
                    </button>
                  </div>
                  {showPasswordReqs && (
                    <div className="password-requirements">
                      <p>{language === 'en' ? 'Password requirements:' : 'Passwortanforderungen:'}</p>
                      <ul>
                        <li>{language === 'en' ? 'At least 8 characters long.' : 'Mindestens 8 Zeichen lang.'}</li>
                        <li>{language === 'en' ? 'Contains at least one uppercase letter.' : 'Enthält mindestens einen Großbuchstaben.'}</li>
                        <li>{language === 'en' ? 'Contains at least one lowercase letter.' : 'Enthält mindestens einen Kleinbuchstaben.'}</li>
                        <li>{language === 'en' ? 'Contains at least one digit.' : 'Enthält mindestens eine Ziffer.'}</li>
                        <li>{language === 'en' ? 'Contains at least one special character (e.g., !@#$%^&*).' : 'Enthält mindestens ein Sonderzeichen (z.B. !@#$%^&*).'}</li>
                      </ul>
                    </div>
                  )}
                </div>

                <div className="terms-text">
                  <p>
                    {language === 'en' 
                      ? 'By continuing, you accept the Terms of Service and acknowledge our Privacy Policy. You agree that you are creating this company account on behalf of your company and are authorized to bind your company.'
                      : 'Durch die Fortsetzung akzeptieren Sie die Nutzungsbedingungen und bestätigen unsere Datenschutzrichtlinie. Sie stimmen zu, dass Sie dieses Unternehmenskonto im Namen Ihres Unternehmens erstellen und berechtigt sind, Ihr Unternehmen zu binden.'}
                  </p>
                </div>
              </div>

              <div className="navigation-buttons exact">
                <div className="step-indicator">
                  <p>
                    {language === 'en' 
                      ? `Step 2 of ${getTotalSteps()}` 
                      : `Schritt 2 von ${getTotalSteps()}`}
                  </p>
                </div>
                <div className="buttons">
                  <button className="previous-button" onClick={handlePrevious}>
                    {language === 'en' ? 'Previous' : 'Zurück'}
                  </button>
                  {getTotalSteps() === 2 ? (
                    <button className="continue-button" onClick={handleSubmit}>
                      {language === 'en' ? 'Let\'s get started' : 'Lass uns anfangen'} <span>&gt;</span>
                    </button>
                  ) : (
                    <button className="continue-button" onClick={handleContinue}>
                      {language === 'en' ? 'Continue' : 'Weiter'} <span>&gt;</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </>
        );
      case 3:
        return (
          <>
            <div className="profile-setup-content">
              <div className="language-toggle-container">
                <button 
                  className="language-toggle"
                  onClick={toggleLanguage}
                >
                  {language === 'en' ? '🇬🇧' : '🇩🇪'}
                </button>
              </div>
              <h1>{language === 'en' ? 'Let\'s talk business' : 'Lassen Sie uns über Ihr Unternehmen sprechen'}</h1>
              <h2>{language === 'en' ? 'We just need a few details and will take it from here.' : 'Wir benötigen nur einige Details und übernehmen dann von hier aus.'}</h2>

              <div className="company-form">
                {error && <p className="error-message">{error}</p>}

                <div className="form-field">
                  <label htmlFor="companyName">{language === 'en' ? 'Company Name' : 'Firmenname'}</label>
                  <input
                    id="companyName"
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    required
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="websiteUrl">{language === 'en' ? 'Website' : 'Webseite'}</label>
                  <input
                    id="websiteUrl"
                    type="url"
                    placeholder="https://..."
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="companyAddress">
                    {language === 'en' ? 'Where is your company headquartered?' : 'Wo hat Ihr Unternehmen seinen Hauptsitz?'}
                  </label>
                  <input
                    id="companyAddress"
                    type="text"
                    placeholder={language === 'en' ? 'Add your address' : 'Fügen Sie Ihre Adresse hinzu'}
                    value={companyAddress}
                    onChange={(e) => setCompanyAddress(e.target.value)}
                  />
                </div>
              </div>

              <div className="navigation-buttons">
                <div className="step-indicator">
                  <p>
                    {language === 'en' 
                      ? `Step 3 of ${getTotalSteps()}` 
                      : `Schritt 3 von ${getTotalSteps()}`}
                  </p>
                </div>
                <div className="buttons">
                  <button className="previous-button" onClick={handlePrevious}>
                    {language === 'en' ? 'Previous' : 'Zurück'}
                  </button>
                  <button className="continue-button" onClick={handleCompanyDetailsSubmit}>
                    {language === 'en' ? 'Continue' : 'Weiter'} <span>&gt;</span>
                  </button>
                </div>
              </div>
            </div>
          </>
        );
      case 4:
        return (
          <>
            <div className="profile-setup-content">
              <div className="language-toggle-container">
                <button 
                  className="language-toggle"
                  onClick={toggleLanguage}
                >
                  {language === 'en' ? '🇬🇧' : '🇩🇪'}
                </button>
              </div>
              <h1>{language === 'en' ? 'Invite your team' : 'Laden Sie Ihr Team ein'}</h1>
              <h2>{language === 'en' ? 'Move faster toward funding. Bring your team on board to unlock easy collaboration.' : 'Schneller zur Finanzierung. Holen Sie Ihr Team an Bord, um eine einfache Zusammenarbeit zu ermöglichen.'}</h2>
              
              <div className="welcome-image-container">
                <img 
                  src={welcomeImage} 
                  alt="Welcome illustration" 
                  className="invite-welcome-image"
                />
              </div>
              
              <div className="invite-team-content">
                <div className="invite-team-info">
                  <p>
                    {language === 'en' 
                      ? 'Everything is set and done. Now you can invite your team members from Profile -> Team -> Invite Users.'
                      : 'Alles ist eingerichtet und erledigt. Jetzt können Sie Ihre Teammitglieder über Profil -> Team -> Benutzer einladen einladen.'}
                  </p>
                </div>

                {error && <p className="error-message">{error}</p>}
              </div>
              
              <div className="navigation-buttons">
                <div className="step-indicator">
                  <p>
                    {language === 'en' 
                      ? `Step 4 of ${getTotalSteps()}` 
                      : `Schritt 4 von ${getTotalSteps()}`}
                  </p>
                </div>
                <div className="buttons">
                  <button className="previous-button" onClick={handlePrevious}>
                    {language === 'en' ? 'Previous' : 'Zurück'}
                  </button>
                  <button className="continue-button" onClick={handleFinalSubmit}>
                    {language === 'en' ? 'Let\'s get started' : 'Lass uns anfangen'} <span>&gt;</span>
                  </button>
                </div>
              </div>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="onboarding-page-wrapper">
      <div className="onboarding-container">
        <div className="onboarding-content">
          {renderStep()}
        </div>
      </div>
    </div>
  );
}

export default OnboardingPage; 